/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface RegionProps {
  id: string;
  name: string;
  abbreviation: string;
  timezoneCd: string | null;
  emSystemDivisionName: string | null;
  dispatchNumber: string | null;
  communicationCenterId: string;
  isTransferCenterRegion: boolean;
  fixedWingPreferredThresholdInNauticalMiles: number | null;
  etcInMinutes: number | null;
  regionGroupId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class RegionRecord extends Record<RegionProps>({
  id: null as any,
  name: null as any,
  abbreviation: null as any,
  timezoneCd: null as any,
  emSystemDivisionName: null as any,
  dispatchNumber: null as any,
  communicationCenterId: null as any,
  isTransferCenterRegion: null as any,
  fixedWingPreferredThresholdInNauticalMiles: null as any,
  etcInMinutes: null as any,
  regionGroupId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    abbreviation: string;
    timezoneCd: string | null;
    emSystemDivisionName: string | null;
    dispatchNumber: string | null;
    communicationCenterId: string;
    isTransferCenterRegion: boolean;
    fixedWingPreferredThresholdInNauticalMiles: number | null;
    etcInMinutes: number | null;
    regionGroupId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): RegionRecord {
    if (json == null) {
      throw new Error('RegionRecord.fromJS: parameter cannot be null');
    }
    return new RegionRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'RegionJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'RegionJson.name'),
      abbreviation: getFromOrThrow(json, (j) => j.abbreviation, 'RegionJson.abbreviation'),
      timezoneCd: json.timezoneCd,
      emSystemDivisionName: json.emSystemDivisionName,
      dispatchNumber: json.dispatchNumber,
      communicationCenterId: getFromOrThrow(
        json,
        (j) =>
          propValueOrDefault(j, 'communicationCenterId', '00000000-0000-0000-0000-000000000000'),
        'RegionJson.communicationCenterId',
      ),
      isTransferCenterRegion: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isTransferCenterRegion', false),
        'RegionJson.isTransferCenterRegion',
      ),
      fixedWingPreferredThresholdInNauticalMiles: json.fixedWingPreferredThresholdInNauticalMiles,
      etcInMinutes: json.etcInMinutes,
      regionGroupId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'regionGroupId', '00000000-0000-0000-0000-000000000000'),
        'RegionJson.regionGroupId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'RegionJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    abbreviation: string;
    timezoneCd: string | null;
    emSystemDivisionName: string | null;
    dispatchNumber: string | null;
    communicationCenterId: string;
    isTransferCenterRegion: boolean;
    fixedWingPreferredThresholdInNauticalMiles: number | null;
    etcInMinutes: number | null;
    regionGroupId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [RegionRecord, Error[]] {
    if (json == null) {
      throw new Error('RegionRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new RegionRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'RegionJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'RegionJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      abbreviation: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.abbreviation, 'RegionJson.abbreviation'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      timezoneCd: tryOrDefault(
        () => json.timezoneCd,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      emSystemDivisionName: tryOrDefault(
        () => json.emSystemDivisionName,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dispatchNumber: tryOrDefault(
        () => json.dispatchNumber,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      communicationCenterId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) =>
              propValueOrDefault(
                j,
                'communicationCenterId',
                '00000000-0000-0000-0000-000000000000',
              ),
            'RegionJson.communicationCenterId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isTransferCenterRegion: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isTransferCenterRegion', false),
            'RegionJson.isTransferCenterRegion',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      fixedWingPreferredThresholdInNauticalMiles: tryOrDefault(
        () => json.fixedWingPreferredThresholdInNauticalMiles,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      etcInMinutes: tryOrDefault(
        () => json.etcInMinutes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      regionGroupId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'regionGroupId', '00000000-0000-0000-0000-000000000000'),
            'RegionJson.regionGroupId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'RegionJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: RegionProps) {
    super(props);
    if (props == null) {
      throw new Error('RegionRecord constructor parameter cannot be null');
    }
  }
}

export abstract class RegionViewModelBase implements RegionProps {
  protected readonly _props: RegionProps;
  private _json?: RegionProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get abbreviation(): string {
    return this._props.abbreviation;
  }
  get timezoneCd(): string | null {
    return this._props.timezoneCd;
  }
  get emSystemDivisionName(): string | null {
    return this._props.emSystemDivisionName;
  }
  get dispatchNumber(): string | null {
    return this._props.dispatchNumber;
  }
  get communicationCenterId(): string {
    return this._props.communicationCenterId;
  }
  get isTransferCenterRegion(): boolean {
    return this._props.isTransferCenterRegion;
  }
  get fixedWingPreferredThresholdInNauticalMiles(): number | null {
    return this._props.fixedWingPreferredThresholdInNauticalMiles;
  }
  get etcInMinutes(): number | null {
    return this._props.etcInMinutes;
  }
  get regionGroupId(): string {
    return this._props.regionGroupId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: RegionProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        abbreviation: this._props.abbreviation,
        timezoneCd: this._props.timezoneCd,
        emSystemDivisionName: this._props.emSystemDivisionName,
        dispatchNumber: this._props.dispatchNumber,
        communicationCenterId: this._props.communicationCenterId,
        isTransferCenterRegion: this._props.isTransferCenterRegion,
        fixedWingPreferredThresholdInNauticalMiles:
          this._props.fixedWingPreferredThresholdInNauticalMiles,
        etcInMinutes: this._props.etcInMinutes,
        regionGroupId: this._props.regionGroupId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllRegionEntities = (state: AppState) => state.entityStores.region.entities;

export const selectRegionById = (id: string) => (state: AppState) =>
  state.entityStores.region.entities.get(id);

export const selectRegionMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.region.meta.get(id);

export const selectRegionAndMetadataById = (id: string) =>
  createSelector(selectRegionById(id), selectRegionMetadataById(id), (region, meta) => ({
    region,
    meta,
  }));

export function selectRegionEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllRegionEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, RegionRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectRegionEntitiesByName(name: string) {
  return createSelector(selectAllRegionEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectRegionEntitiesByAbbreviation(abbreviation: string) {
  return createSelector(selectAllRegionEntities, (entities) =>
    entities.filter((e) => e.abbreviation === abbreviation).valueSeq(),
  );
}

export function selectRegionEntitiesByTimezoneCd(timezoneCd: string) {
  return createSelector(selectAllRegionEntities, (entities) =>
    entities.filter((e) => e.timezoneCd === timezoneCd).valueSeq(),
  );
}

export function selectRegionEntitiesByEmSystemDivisionName(emSystemDivisionName: string) {
  return createSelector(selectAllRegionEntities, (entities) =>
    entities.filter((e) => e.emSystemDivisionName === emSystemDivisionName).valueSeq(),
  );
}

export function selectRegionEntitiesByDispatchNumber(dispatchNumber: string) {
  return createSelector(selectAllRegionEntities, (entities) =>
    entities.filter((e) => e.dispatchNumber === dispatchNumber).valueSeq(),
  );
}

export function selectRegionEntitiesByCommunicationCenterId(communicationCenterId: string) {
  return createSelector(selectAllRegionEntities, (entities) =>
    entities.filter((e) => e.communicationCenterId === communicationCenterId).valueSeq(),
  );
}

export function selectRegionEntitiesByIsTransferCenterRegion(isTransferCenterRegion: boolean) {
  return createSelector(selectAllRegionEntities, (entities) =>
    entities.filter((e) => e.isTransferCenterRegion === isTransferCenterRegion).valueSeq(),
  );
}

export function selectRegionEntitiesByFixedWingPreferredThresholdInNauticalMiles(
  fixedWingPreferredThresholdInNauticalMiles: number,
) {
  return createSelector(selectAllRegionEntities, (entities) =>
    entities
      .filter(
        (e) =>
          e.fixedWingPreferredThresholdInNauticalMiles ===
          fixedWingPreferredThresholdInNauticalMiles,
      )
      .valueSeq(),
  );
}

export function selectRegionEntitiesByEtcInMinutes(etcInMinutes: number) {
  return createSelector(selectAllRegionEntities, (entities) =>
    entities.filter((e) => e.etcInMinutes === etcInMinutes).valueSeq(),
  );
}

export function selectRegionEntitiesByRegionGroupId(regionGroupId: string) {
  return createSelector(selectAllRegionEntities, (entities) =>
    entities.filter((e) => e.regionGroupId === regionGroupId).valueSeq(),
  );
}

export function selectRegionEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllRegionEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
